import { useEffect, useState } from 'react';
import { useI18n } from '@react/i18n';
import { validateCzBankAccount, validateIban } from '../utils/bankAccount';
import { LookupData, Page, UserInput } from '../types';
import { UpdateUserInput } from '../VratkyApp';
import { BankAccountSection, FormError, FormErrorAlert, prefillBankAccount, requiresAccountNumber } from './BankAccountSection';

interface Props {
    userInput: UserInput;
    update: UpdateUserInput;
    data: LookupData;
    link: (page: Page) => void;
}

const NO_DETAIL_REASONS = ['14_dni', 'poskozeno_preprava'];

export function VratkaForm({ userInput, update, data, link }: Props) {
    const t = useI18n();
    const [error, setError] = useState<FormError | null>(null);
    const required = requiresAccountNumber(data);
    const jeFirma = Boolean(data.objednavka.fa_dic || data.objednavka.fa_ico);
    const needsReason = !NO_DETAIL_REASONS.includes(userInput.duvod_vraceni);

    useEffect(() => {
        // navrat z reklamace: duvod 'reklamace' u vratky nedava smysl
        if (userInput.duvod_vraceni === 'reklamace') {
            update('duvod_vraceni', '14_dni');
        }
        // firma nema narok na vraceni do 14 dni bez udani duvodu
        if (jeFirma && userInput.duvod_vraceni === '14_dni') {
            update('duvod_vraceni', 'chyba_zakaznik');
        }
        prefillBankAccount(data, userInput, update);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const checkAndMove = () => {
        const isCz = data.objednavka.shop_country_code === 'cz';
        if (needsReason && !userInput.duvod_vraceni_detail) {
            setError('duvod_missing');
        } else if (userInput.has_special_conditions && !userInput.special_conditions) {
            setError('special_conditions_missing');
        } else if (!userInput.cislo_uctu_user && isCz && (!userInput.no_account_number || required)) {
            setError('ucet_missing');
        } else if (!userInput.iban_user && !isCz && (!userInput.no_account_number || required)) {
            setError('iban_missing');
        } else if (!isCz && !userInput.no_account_number) {
            if (!validateIban(userInput.iban_user)) {
                setError('wrong_iban');
            } else {
                link('contact');
            }
        } else if (isCz && !userInput.no_account_number) {
            if (!validateCzBankAccount(userInput.cislo_uctu_user)) {
                setError('wrong_account_number');
            } else {
                link('contact');
            }
        } else {
            link('contact');
        }
    };

    return (
        <div className="vr-step">
            <h3>{t('Důvod vratky')}</h3>

            <div className="vr-field">
                <label>{t('Důvod vrácení')}<span className="vr-required">*</span></label>
                <select
                    className="form-control"
                    value={userInput.duvod_vraceni}
                    onChange={(e) => update('duvod_vraceni', e.target.value)}
                >
                    {!jeFirma && <option value="14_dni">{t('Bez důvodu do 14ti dní (pouze koncový zákazník)')}</option>}
                    <option value="poskozeno_preprava">{t('Poškozeno při přepravě')}</option>
                    <option value="chyba_bola">{t('Chyba na straně BOLA spol s.r.o.')}</option>
                    <option value="chyba_zakaznik">{t('Chyba na straně zákazníka')}</option>
                    <option value="jiny">{t('Jiný')}</option>
                </select>
            </div>

            <div className="vr-field">
                <label>{t('Prosíme rozepište důvod vrácení')}{needsReason && <span className="vr-required">*</span>}</label>
                <textarea
                    maxLength={500}
                    className="form-control"
                    rows={2}
                    placeholder="..."
                    value={userInput.duvod_vraceni_detail}
                    onChange={(e) => update('duvod_vraceni_detail', e.target.value)}
                />
            </div>

            <BankAccountSection userInput={userInput} update={update} data={data} />

            {error === 'duvod_missing' && !userInput.duvod_vraceni_detail && (
                <div className="vr-alert vr-alert--danger" role="alert">{t('Prosím popište důvod vrácení zboží.')}</div>
            )}
            <FormErrorAlert error={error} userInput={userInput} />

            <div className="vr-actions">
                <button type="button" className="vr-btn vr-btn--secondary" onClick={() => link('product_selector')}>{t('Zpět')}</button>
                <button type="button" className="vr-btn vr-btn--primary" onClick={checkAndMove}>{t('Pokračovat')}</button>
            </div>
        </div>
    );
}
